import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { tenantApi } from '../../api/endpoints';
import { useI18n } from '../../i18n';
import { Checkbox, Dialog, Icon, type IconName } from '../../ui';
import { useCurrentCompany } from '../companies/hooks';
import { copyName, rememberedChoice, type ConflictChoice, type ExistingFile } from './name-conflict';

/** What the uploader asks about one file whose name is taken (TF-105). */
export interface NameConflictQuestion {
  tenantId: string;
  file: { name: string; size: number };
  existing: ExistingFile;
  /** Started from a record (a deal, a task...): the existing file can be linked there instead. */
  canLink: boolean;
  /** Further files of the same batch still to go, for "apply to all". */
  remaining: number;
}

export interface NameConflictAnswer {
  choice: ConflictChoice;
  applyToAll: boolean;
}

/**
 * Asks what to do with a file whose name is taken where it is going: use the file already there
 * (linked to the record it was uploaded from), add it as a copy with the date and time in its name,
 * or add it as a new version of the one there. The last choice of the session is the default button,
 * never applied unasked. Cancel stops this upload only.
 */
export function NameConflictDialog({ question, onAnswer }: { question: NameConflictQuestion; onAnswer: (answer: NameConflictAnswer | null) => void }) {
  const { t, date, bytes } = useI18n();
  const [applyToAll, setApplyToAll] = useState(false);
  const { existing, file, canLink, remaining } = question;
  // Who added the one there, by name where the company's list has them.
  const { isMember } = useCurrentCompany();
  const members = useQuery({ queryKey: ['members', question.tenantId], queryFn: () => tenantApi.members(question.tenantId), staleTime: 5 * 60_000, enabled: isMember });
  const addedBy = members.data?.find((member) => member.userId === existing.createdBy);
  const who = addedBy ? (addedBy.displayName ?? addedBy.email) : null;
  const folder = existing.folderPath.length > 0 ? existing.folderPath.join(' / ') : t('files.root');

  const choices: { choice: ConflictChoice; icon: IconName; label: string; hint: string }[] = [
    ...(canLink ? [{ choice: 'existing' as const, icon: 'link' as const, label: t('upload.conflict.useExisting'), hint: t('upload.conflict.useExistingHint') }] : []),
    { choice: 'copy', icon: 'file_copy', label: t('upload.conflict.copy'), hint: copyName(file.name, new Date()) },
    { choice: 'version', icon: 'history', label: t('upload.conflict.version'), hint: t('upload.conflict.versionHint') },
  ];
  const remembered = rememberedChoice();
  const preferred = choices.some((option) => option.choice === remembered) ? remembered : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onAnswer(null)} title={t('upload.conflict.title')} icon="file_copy">
      <dl className="conflict-files">
        <div>
          <dt>{t('upload.conflict.there')}</dt>
          <dd>
            <strong>{existing.name}</strong>
            <span className="small muted">
              {[
                existing.sizeBytes !== null ? bytes(existing.sizeBytes) : null,
                who ? t('upload.conflict.addedBy', { date: date(existing.createdAt), name: who }) : t('upload.conflict.added', { date: date(existing.createdAt) }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className="small muted">{t('upload.conflict.inFolder', { folder })}</span>
          </dd>
        </div>
        <div>
          <dt>{t('upload.conflict.yours')}</dt>
          <dd>
            <strong>{file.name}</strong>
            <span className="small muted">{bytes(file.size)}</span>
          </dd>
        </div>
      </dl>
      <ul className="conflict-choices">
        {choices.map((option) => (
          <li key={option.choice}>
            <button
              type="button"
              className={option.choice === preferred ? 'conflict-choice preferred' : 'conflict-choice'}
              autoFocus={option.choice === preferred}
              onClick={() => onAnswer({ choice: option.choice, applyToAll })}
            >
              <Icon name={option.icon} size={24} />
              <span>
                <strong>{option.label}</strong>
                <span className="small muted">{option.hint}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {remaining > 0 && <Checkbox label={t('upload.conflict.applyToAll', { count: remaining })} checked={applyToAll} onChange={setApplyToAll} />}
      <div className="dialog-foot">
        <button type="button" className="btn" onClick={() => onAnswer(null)}>
          {t('upload.conflict.cancel')}
        </button>
      </div>
    </Dialog>
  );
}
